import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import { useStore } from '../../store';
import { brand, brandOr } from '../../utils/brand';

// External portal — the ONLY screen external accountants / auditors see. Every call goes to
// /api/ext (server-side row-level scoped to their assigned clients). They can review their clients'
// tasks and update status (review / sign step). They can never reach internal data.

// 10.5 → 11.5px (Calem, report 66c9d174, 2nd opening: "Do the same for all other menus in the SLF system").
const th: React.CSSProperties = { fontSize: 11.5, fontWeight: 700, color: 'var(--text-secondary)', padding: '8px 10px', borderBottom: '1px solid var(--border-strong)', textAlign: 'left', whiteSpace: 'nowrap', background: 'var(--c-gray-50)' };
const td: React.CSSProperties = { fontSize: 12.5, padding: '8px 10px', borderBottom: '1px solid var(--border)', verticalAlign: 'middle' };
const fmtDate = (ymd: string) => { const p = String(ymd || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : (ymd || '—'); };
const CYCLE_LABEL: Record<string, string> = { monthly: 'Monthly', half_year: 'Half-year', annual: 'Annual', one_off: 'One-off' };

function TaskStatus({ status, overdue, onSet }: { status: string; overdue: boolean; onSet: (s: string) => void }) {
  const map: Record<string, { bg: string; c: string }> = {
    pending: overdue ? { bg: '#FEF2F2', c: '#DC2626' } : { bg: '#FFFBEB', c: '#B45309' },
    in_progress: { bg: '#E6F1FB', c: '#185FA5' }, blocked: { bg: '#FEF2F2', c: '#DC2626' },
    done: { bg: '#ECFDF5', c: '#15803D' }, na: { bg: 'var(--c-gray-100)', c: 'var(--text-tertiary)' },
  };
  const s = map[status] || map.pending;
  return (
    <select value={status} onChange={e => onSet(e.target.value)} style={{ padding: '4px 8px', fontSize: 11.5, fontWeight: 700, borderRadius: 8, background: s.bg, color: s.c, border: `1px solid ${s.c}33`, cursor: 'pointer' }}>
      <option value="pending">{overdue ? 'Overdue' : 'Pending'}</option>
      <option value="in_progress">In progress</option>
      <option value="blocked">Blocked</option>
      <option value="done">Reviewed / done</option>
      <option value="na">N/A</option>
    </select>
  );
}

function ClientPanel({ clientId, onClose }: { clientId: string; onClose: () => void }) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true); api.extClient(clientId).then(setData).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, [clientId]); // eslint-disable-line
  const setStatus = async (t: any, status: string) => {
    setData((d: any) => ({ ...d, tasks: d.tasks.map((x: any) => x.id === t.id ? { ...x, status } : x) }));
    await api.extSetTask(t.id, status).catch(() => load());
  };
  const c = data?.client;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1500, display: 'flex', justifyContent: 'flex-end' }} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: 780, maxWidth: '96vw', height: '100%', background: 'var(--bg-app)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}>{c?.name || '…'}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 2 }}>{c?.tax_id ? `Tax ID ${c.tax_id}` : ''}{data?.my_role ? ` · you: ${data.my_role}` : ''}</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: 18 }}><i className="ti ti-x" /></button>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          {loading ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>
            : !data?.tasks?.length ? <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>No tasks need you on this client yet.</div>
              : (
                <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--bg-surface)', borderRadius: 10, overflow: 'hidden' }}>
                  <thead><tr><th style={th}>Task</th><th style={th}>Cycle</th><th style={th}>Period</th><th style={th}>Due</th><th style={{ ...th, width: 150 }}>Status</th></tr></thead>
                  <tbody>
                    {data.tasks.map((t: any) => (
                      <tr key={t.id}>
                        <td style={{ ...td, fontWeight: 600 }}>{t.label}</td>
                        <td style={td}>{CYCLE_LABEL[t.cycle] || t.cycle}</td>
                        <td style={{ ...td, color: 'var(--text-tertiary)' }}>{t.period}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: t.overdue ? 700 : 400, color: t.overdue ? '#DC2626' : 'var(--text-primary)' }}>{fmtDate(t.due_date)}</td>
                        <td style={td}><TaskStatus status={t.status} overdue={t.overdue} onSet={s => setStatus(t, s)} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
        </div>
      </div>
    </div>
  );
}

export default function ExternalPortal() {
  const logout = useStore(s => s.logout);
  const [me, setMe] = useState<any>(null);
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<string>('');

  const load = () => { setLoading(true); Promise.all([api.extMe(), api.extClients()]).then(([m, c]: any) => { setMe(m); setRows(c.rows || []); }).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-app)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        <img src="/logo.png" alt={brandOr('Logo')} style={{ width: 28, height: 28, objectFit: 'contain' }} />
        <div style={{ fontSize: 15, fontWeight: 700 }}>{[brand(), 'External portal'].filter(Boolean).join(' · ')}</div>
        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{me ? `${me.name} · ${me.role}` : ''}</span>
        <div style={{ flex: 1 }} />
        <button onClick={logout} style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}><i className="ti ti-logout" /> Sign out</button>
      </header>

      <div style={{ flex: 1, overflow: 'auto', padding: '16px 18px' }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Your clients <span style={{ color: 'var(--text-tertiary)', fontWeight: 600 }}>({rows.length})</span></div>
        {loading ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>
          : !rows.length ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>No clients have been assigned to you yet.</div>
            : (
              <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--bg-surface)' }}>
                  <thead><tr>
                    <th style={th}>Client</th><th style={th}>Your role</th>
                    <th style={{ ...th, textAlign: 'right' }}>Tasks</th><th style={{ ...th, textAlign: 'right' }}>Done</th><th style={{ ...th, textAlign: 'right' }}>Overdue</th><th style={{ ...th, width: 40 }} />
                  </tr></thead>
                  <tbody>
                    {rows.map(r => (
                      <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => setOpen(r.id)} onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-active)')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                        <td style={td}><span style={{ fontSize: 11, color: 'var(--text-tertiary)', marginRight: 8 }}>#{String(r.no || 0).padStart(4, '0')}</span><span style={{ fontWeight: 600 }}>{r.name}</span></td>
                        <td style={td}>{r.my_role}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{r.tasks}</td>
                        <td style={{ ...td, textAlign: 'right', color: '#15803D' }}>{r.done || ''}</td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: r.overdue ? 700 : 400, color: r.overdue ? '#DC2626' : 'var(--text-tertiary)' }}>{r.overdue || ''}</td>
                        <td style={{ ...td, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-chevron-right" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </div>

      {open && <ClientPanel clientId={open} onClose={() => setOpen('')} />}
    </div>
  );
}
